<template>
  <b-modal
    id="pay-license-modal"
    ref="pay-license-modal"
    centered
    @show="show"
    @hidden="visible = false"
    @ok="handleOk"
    body-class="nice-modal"
    :visible="visible"
    :ok-title="
      !(paymentError || savingError) ? $t('button.continue') : $t('button.close')
    "
    :cancel-title="$t('button.cancel')"
    :busy="!intro && !paymentError && !savingError"
    no-close-on-backdrop
    :no-close-on-esc="!intro"
    :ok-only="paymentError || savingError"
  >
    <template v-slot:modal-header>
      <div>
        <h5>{{ $t('license.payment.h') }}</h5>
      </div>
    </template>

    <div>
      <b-img
        v-if="!paymentError && !savingError"
        class="status-icon mb-4"
        center
        src="/secure-payment.svg"
        fluid
        alt="Responsive image"
      ></b-img>
      <b-img
        v-else
        class="status-icon mb-4"
        center
        src="/payment-failed.svg"
        fluid
        alt="Responsive image"
      ></b-img>
      <div class="text-center">
        <div :class="[intro ? 'b-block' : 'd-none']">
          <p class="mb-4">{{ $t('license.payment.intro.msg') }}</p>
          <p class="text-secondary">{{ $t("license.payment.intro.msg2") }}</p>
        </div>
        <div :class="[processing ? 'b-block' : 'd-none']">
          <template v-if="saving">
            <p class="font-weight-bold mb-4">{{ $t('license.payment.progress.saving') }}</p>
          </template>
          <template v-else>
            <p v-if="!redirecting" class="font-weight-bold mb-4">
              {{ $t('license.payment.progress.preparation') }}
            </p>
            <p v-else class="font-weight-bold mb-4">{{ $t('license.payment.progress.redirecting') }}</p>
          </template>
          <b-spinner class="mb-4" label="Spinning"></b-spinner>
        </div>
        <div :class="[paymentError || savingError ? 'b-block' : 'd-none']">
          <p class="mb-4" v-if="paymentError">
            {{ $t('license.payment.error.preparation') }}
          </p>
          <p v-else>
            {{ $t('license.payment.error.saving') }}
          </p>
          <p class="text-secondary">
            {{ $t('license.payment.error.msg') }}
          </p>
        </div>
      </div>
    </div>
    <stripe-checkout
      ref="checkoutRef"
      :pk="publishableKey"
      :sessionId="stripe_session_id"
      @loading="(v) => (loading = v)"
      @error="handleStripeLoadError"
    />
  </b-modal>
</template>

<script>
import { StripeCheckout } from "@vue-stripe/vue-stripe";
import { wait } from "@/plugins/request";
import { NORMAL_DESIGN_DELAY } from "@/globals";
import { toRequestObj } from "@/components/rdbm/license.js";
import {
  URL_SAVE_LICENSE,
  URL_UPDATE_LICENSE,
  URL_PAY_LICENSE,
} from "@/globals";

export default {
  name: "PayLicenseModal",
  components: { StripeCheckout },
  props: ["licenseWrapper"],
  data() {
    return {
      publishableKey: null,
      license: this.licenseWrapper,
      stripe_session_id: null,
      loading: false,
      visible: false,
      intro: false,
      saving: false,
      processing: false,
      redirecting: false,
      savingError: false,
      paymentError: false,
    };
  },
  methods: {
    show() {
      this.intro = true;
      this.saving = false;
      this.processing = false;
      this.redirecting = false;
      this.savingError = false;
      this.paymentError = false;
      this.visible = true;
    },
    execute(license) {
      if (license) this.license = license;
      this.show();
    },
    handleOk(bvModalEvt) {
      if (this.intro) {
        // Prevent modal from closing
        bvModalEvt.preventDefault();
        this.intro = false;
        if (this.license.isChangedAndValid()) {
          this.saving = true;
          this.save();
        } else {
          this.processing = true;
          this.pay();
        }
      }
    },
    save() {
      if (this.license.data._new) {
        // В текущей реализации не должен вызываться, потому что лицензий сначала сохраняется
        // в одном из видов (обычная/зашифрованная) и только потом доступны другие действия
        // включая оплату.
        this.$post(
          URL_SAVE_LICENSE,
          toRequestObj(this.license.data),
          true
        )
          .then((result) => {
            this.$emit("item-saved", result);
            this.saving = false;
            this.processing = true;
            this.pay();
          })
          .catch((error) => {
            this.$alert.show(error);
            this.savingError = true;
            this.processing = false;
          });
      } else {
        this.$patch(
          URL_UPDATE_LICENSE + this.license.data.id,
          toRequestObj(this.license.data),
          true
        )
          .then((result) => {
            this.$emit("item-saved", result);
            this.saving = false;
            this.processing = true;
            this.pay();
          })
          .catch((error) => {
            this.$alert.show(error);
            this.savingError = true;
            this.processing = false;
          });
      }
    },
    pay() {
      this.$get(
        URL_PAY_LICENSE.format(this.license.data.id),
        true,
        true,
        true
      )
        .then((result) => {
          this.redirecting = true;
          this.stripe_session_id = result.session_id;
          wait(NORMAL_DESIGN_DELAY).then(() => {
            // You will be redirected to Stripe's secure checkout page
            this.$refs.checkoutRef.redirectToCheckout();
          });
        })
        .catch((error) => {
          this.$alert.show(error);
          this.paymentError = true;
          this.processing = false;
        });
    },
    handleStripeLoadError() {
      console.log("Could not load Stripe component!")
    }
  },
  mounted() {
    let script = document.createElement('script')
    script.onload = function(){
      if (window._STRIPE_KEY_NAME) {
        this.publishableKey = process.env[window._STRIPE_KEY_NAME];
      }
      // Если переменная не задана, будет ключ для разработки
      if (!this.publishableKey) {
        this.publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_DEV_KEY
      }
    }.bind(this)
    script.setAttribute('src', 'https://' + window.location.host + '/sso-signin.js') // '/stripe-key-name.js')
    document.head.appendChild(script)
  }
};
</script>

<style scoped>
.modal-header p {
  margin: 0;
}
.status-icon {
  height: 120px;
  width: 120px;
}
</style>